import React, { useState, useEffect } from "react";
import "./groupdesign/Tab.css";
import { useParams } from "react-router-dom";
import GroupPostList from "../groups/GroupPosts";
import Pendingrequest from "./Pendingrequest";
import GroupPostForm from "./GroupPostForm";
import InviteUser from "./InviteUser";
import GroupInfo from "./Groupinfo";
import { getApiBaseUrl } from "../../config/api";
import { isAdmin } from "../../utils/userRole";

const API_URL = getApiBaseUrl();

export default function Tabs({ user, onLike }) {
  const { groupId } = useParams();
  const numericGroupId = Number(groupId);

  const [activeTab, setActiveTab] = useState("groups");
  const [posts, setPosts] = useState([]);
  const [likes, setLikes] = useState({});
  const [userLiked, setUserLiked] = useState({});
  const [repliesByPostId, setRepliesByPostId] = useState({});
  const [members, setMembers] = useState([]);
  const [groupDescription, setGroupDescription] = useState("");
  const [groupName, setGroupName] = useState("");
  const [loadingDesc, setLoadingDesc] = useState(true);
  const [groupLocked, setGroupLocked] = useState(false);
  const [scheduledDeleteAt, setScheduledDeleteAt] = useState(null);
  /** Bumps so GroupPosts re-fetches after create/edit (it keeps its own list state). */
  const [listRefreshKey, setListRefreshKey] = useState(0);

  // 🔹 Fetch group description and members
  const fetchGroupInfo = async () => {
    if (!groupId) return;
    try {
      const res = await fetch(`${API_URL}/get_group_info.php?group_id=${numericGroupId}`);
      const data = await res.json();
      if (data.success) {
        setGroupDescription(data.group.description || "No description available.");
        setGroupName(data.group.name || "Untitled Group");
        setMembers(data.group.members || []);
        setGroupLocked(Boolean(data.group.is_deactivated || data.group.pending_delete));
        setScheduledDeleteAt(data.group.scheduled_delete_at || null);
      } else {
        setGroupDescription("Failed to load group info.");
      }
    } catch (err) {
      console.error("Error fetching group info:", err);
      setGroupDescription("Error loading group info.");
    } finally {
      setLoadingDesc(false);
    }
  };

  // 🔹 Fetch posts
  const fetchPosts = async () => {
    if (!groupId || !user?.id) return;
    try {
      const res = await fetch(
        `${API_URL}/get_group_posts.php?group_id=${numericGroupId}&user_id=${user.id}`
      );
      const data = await res.json();
      if (data.success) updateStateFromPosts(data.posts);
    } catch (err) {
      console.error("Error fetching posts:", err);
    }
  };

  const refreshPostList = async () => {
    await fetchPosts();
    setListRefreshKey((k) => k + 1);
  };

  useEffect(() => {
    fetchPosts();
    fetchGroupInfo();
  }, [groupId, numericGroupId, user]);

  // 🔹 Helper: update likes, replies, posts state
  const updateStateFromPosts = (newPosts) => {
    setPosts(newPosts);

    const likeCounts = {};
    const likedStatus = {};
    const repliesMap = {};

    newPosts.forEach((post) => {
      const postKey = `post_${post.id}`;
      likeCounts[postKey] = post.like_count || 0;
      likedStatus[postKey] = post.user_liked || false;

      const replies = post.replies || [];
      repliesMap[post.id] = replies;

      replies.forEach((reply) => {
        const replyKey = `reply_${reply.id}`;
        likeCounts[replyKey] = reply.like_count || 0;
        likedStatus[replyKey] = reply.user_liked || false;
      });
    });

    setLikes(likeCounts);
    setUserLiked(likedStatus);
    setRepliesByPostId(repliesMap);
  };

  // 🔹 Add new post instantly (no refresh)
  const handleNewPost = (newPost) => {
    setPosts((prev) => [newPost, ...prev]);
    updateStateFromPosts([newPost, ...posts]);
  };

  // 🔹 Add new reply instantly
  const handleNewReply = (postId, newReply) => {
    setRepliesByPostId((prev) => ({
      ...prev,
      [postId]: [...(prev[postId] || []), newReply],
    }));
  };

  // 🔹 Toggle like instantly
  const handleLikeToggle = (itemKey, isLiked) => {
    setLikes((prev) => ({
      ...prev,
      [itemKey]: (prev[itemKey] || 0) + (isLiked ? 1 : -1),
    }));
    setUserLiked((prev) => ({
      ...prev,
      [itemKey]: isLiked,
    }));
  };

  return (
    <div className="tabs-container">
      <GroupInfo
        groupId={groupId}
        members={members || []}
        user={user}
        onGroupUpdated={fetchGroupInfo}
      />

      <div className="tab-header" role="tablist" aria-label="Group sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "groups"}
          className={activeTab === "groups" ? "active" : ""}
          onClick={() => setActiveTab("groups")}
        >
          Discussion
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "members"}
          className={activeTab === "members" ? "active" : ""}
          onClick={() => setActiveTab("members")}
        >
          Members
        </button>

        {isAdmin(user) && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "pending"}
            className={activeTab === "pending" ? "active" : ""}
            onClick={() => setActiveTab("pending")}
          >
            Pending Requests
          </button>
        )}
      </div>

      <div className="tab-content">
        {activeTab === "groups" && (
          <div className="group-discussion">
            <div className="group-discussion__composer">
              {groupLocked ? (
                <p style={{ color: "#9a3412", margin: 0 }}>
                  {scheduledDeleteAt
                    ? "This group is scheduled for deletion. Posting is disabled."
                    : "This group is deactivated. Posting is disabled."}
                </p>
              ) : user && groupId ? (
                <GroupPostForm
                  user={user}
                  groupId={groupId}
                  groupName={groupName}
                  onRefresh={refreshPostList}
                />
              ) : (
                <p style={{ color: "#b91c1c", margin: 0 }}>
                  Please log in and select a group to post.
                </p>
              )}
            </div>

            <GroupPostList
              groupId={numericGroupId}
              user={user}
              posts={posts}
              likes={likes}
              userLiked={userLiked}
              repliesByPostId={repliesByPostId}
              onReplyAdded={handleNewReply}
              onLikeToggle={handleLikeToggle}
              onRefresh={refreshPostList}
              refreshKey={listRefreshKey}
            />
          </div>
        )}

        {activeTab === "members" && (
          <InviteUser groupId={numericGroupId} user={user} adminId={user.id} groupLocked={groupLocked} />
        )}

        {activeTab === "pending" && isAdmin(user) && (
          <div className="group-pending-panel">
            <h3>Pending Requests</h3>
            <Pendingrequest
              groupId={numericGroupId}
              user={user}
              adminId={user.id}
              showRequests={true}
            />
          </div>
        )}

        {activeTab === "description" && (
          <div className="description-tab">
            <h3>{groupName}</h3>
            {loadingDesc ? (
              <p>Loading description...</p>
            ) : (
              <p>{groupDescription}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
